import React, { useEffect } from "react";
import "./App.css";
import Header from "./components/Header";
import CTA from "./components/CTA";
import Hero from "./components/Hero";
import Projects from "./components/Projects";
import Services from "./components/Services";
import Footer from "./components/Footer";
import About from "./components/About";
import OurPartners from "./components/Partners";
import { Service, Project, Stat, TeamMember, Partners } from "./types";
import "./styles/globals.css";
import Team from "./components/Team";

// Add Font Awesome to head
const addFontAwesome = () => {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href =
    "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css";
  document.head.appendChild(link);

  const font1 = document.createElement("link");
  font1.rel = "stylesheet";
  font1.href =
    "https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&display=swap";
  document.head.appendChild(font1);

  const font2 = document.createElement("link");
  font2.rel = "stylesheet";
  font2.href =
    "https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap";
  document.head.appendChild(font2);
};

const App: React.FC = () => {
  useEffect(() => {
    addFontAwesome();
  }, []);

  const companyName = "TECHPIONEERS | PROJECTS";

  const heroStats: Stat[] = [
    { id: 1, number: "128", label: "Employees" },
    { id: 2, number: "8", label: "Years Experience" },
    { id: 4, number: "10", label: "Projects Completed" },
  ];

  const primaryServices: Service[] = [
    {
      id: 1,
      title: "Contract Mining",
      description:
        "Full-service mining operations with experienced crews and advanced equipment.",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/miningcontractor.jpg",
    },
    {
      id: 2,
      title: "Material Handling",
      description:
        "Efficient material movement and processing solutions for mining operations.",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/services2.jpeg",
    },
    {
      id: 3,
      title: "Coal Transportation & Haulage",
      description: "Reliable coal transport and logistics management services.",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/gallery1.jpeg",
    },
    {
      id: 4,
      title: "Mobile Crushing And Screen",
      description:
        "Mobile crushing and screening solutions for material processing.",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/materialHandling.jpg",
    },
    {
      id: 5,
      title: "De-setting Of Pollution Control Dams",
      description:
        "Environmental remediation and pollution control dam management.",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/De-settingOfPollutionControlDams.jpg",
    },
    {
      id: 6,
      title: "Road Maintenance",
      description: "Comprehensive road construction and maintenance services.",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/roadmarkings.jpeg",
    },
    {
      id: 7,
      title: "Dust Suppression",
      description: "Environmental dust control and suppression systems.",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/dust.jpg",
    },
    {
      id: 8,
      title: "Road Signage And Demarcation",
      description:
        "Professional road marking and signage installation services.",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/roadSign.jpg",
    },
    {
      id: 9,
      title: "Revbolt & Ground Support Technologies",
      description: "Advanced ground support and reinforcement solutions.",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/gallery1.jpeg",
    },
    {
      id: 10,
      title: "Dewatering & Slurry Pumps",
      description: "Water management and slurry handling pump systems.",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/dewatering.jpg",
    },
  ];

  const equipmentRental: Service[] = [
    {
      id: 1,
      title: "Front End Loaders",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/frontendload.jpg",
    },
    {
      id: 2,
      title: "Articulated Dump Trucks",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/dumptruck.jpg",
    },
    {
      id: 3,
      title: "Excavators",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/Escavator.jpg",
    },
    {
      id: 4,
      title: "Rigid Dump Trucks",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/RigidDumpTrucks.jpeg",
    },
    {
      id: 5,
      title: "Graders",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/sany.jpg",
    },
    {
      id: 6,
      title: "TLB Tractor Loader Backhoe",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/TLB.jpeg",
    },
    {
      id: 7,
      title: "Dozers",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/Bulldozer.jpg",
    },
    {
      id: 8,
      title: "Compact Rollers",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/roller.jpg",
    },
    {
      id: 9,
      title: "18 Cube Tipper Trucks",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/tipper.jpg",
    },
    {
      id: 10,
      title: "Water Tankers",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/water%20tank.jpg",
    },
  ];

  const isoImplementation: Service[] = [
    {
      id: 1,
      icon: "fas fa-satellite-dish",
      title: "ISO 9001",
      description: "Quality Management Systems",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/iso9001.png",
      // image: "https://www.arenasolutions.com/wp-content/uploads/what-is-iso-9001-compliance.png",
    },
    {
      id: 2,
      icon: "fas fa-hard-hat",
      title: "ISO 14001",
      description: "Environmental Management",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/iso14001.png",
    },
    {
      id: 3,
      icon: "fas fa-water",
      title: "ISO 45001",
      description: "Occupational Health & Safety",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/iso45001.png",
    },
  ];

  const projects: Project[] = [
    {
      id: 1,
      title: "Gugulethu Colliery",
      description:
        "Comprehensive material handling operations including coal transportation and equipment management.",
      scope: "Material Handling & Logistics",
      equipment: "Front End Loaders, Dump Trucks",
      volume: "200,000 tons ROM and 150,000 tons Product per month",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/gugletho.jpeg",
    },
    {
      id: 2,
      title: "Risenga Colliery",
      description:
        "Techpioneers offers a range of services including stockpile management, screening and crushing, contract mining and road maintenance.",
      scope: "Material Handling & Logistics",
      equipment: "Front End Loaders & Dump Trucks & Haul Trucks",
      volume: "100,000 tons ROM and 100,000 tons Product per month",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/risenga.jpeg",
    },
    {
      id: 3,
      title: "Mavungwani Colliery",
      description:
        "Specialized discard handling and environmental management services.",
      scope: "Material Handling & Discard Handling",
      equipment: "Front End Loaders & Dump Trucks",
      volume: "100,000 tons Discard per month",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/mavungwani.jpeg",
    },
    {
      id: 4,
      title: "Strathrae Colliery",
      description:
        "Specialized discard handling and environmental management services.",
      scope: "Material Handling & Discard Handling",
      equipment: "Front End Loaders & Dump Trucks & Haul Trucks",
      // volume: "100,000 tons Discard per month",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/materialHandling.jpg",
    },
    {
      id: 5,
      title: "Thungela",
      iso: "ISO 9001/ISO 14001/ISO 45001",
      scope: "ISO Implementation",
      companies: "206",
      // volume: "100,000 tons Discard per month",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/services/services5.jpeg",
    },
  ];

  const teammates: TeamMember[] = [
    {
      id: 1,
      fullName: "Thulani E. Thukwana",
      position: "Chief Executive Officer",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/team/icon.jpg",
    },
    {
      id: 2,
      fullName: "Sbusiso Mashinini",
      position: "General Manager",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/team/icon.jpg",
    },
    {
      id: 3,
      fullName: "Vuyiswa Songo",
      position: "Accounting & Finance",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/team/icon2.jpg",
    },
    {
      id: 4,
      fullName: "Siyabonga Mahlangu",
      position: "Project Executive",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/team/icon.jpg",
    },
    {
      id: 5,
      fullName: "Samuel Phoku",
      position: "Engineering Executive",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/team/icon.jpg",
    },
    {
      id: 6,
      fullName: "Sphesihle Mdluli",
      position: "Human Resources",
      image:
        "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/team/icon2.jpg",
    },
  ];

  const aboutSubsections = [
    {
      title: "STRONG & REPUTABLE",
      content:
        "Techpioneers comprises of young, dynamic team full of energy, enthusiasm and ideas with extensive material handling and haulage experience amongst the team.",
    },
    {
      title: "HONEST & DEPENDABLE",
      content:
        "Techpioneers has a team that is reliable and dependable. Clients are sure to get the best of both in their engagements with us. Projects are executed effectively and on time. Clients can rely on our services and expertise and depend on our ability to make their projects a success.",
    },
  ];

  const partners: Partners[] = [
    {
      id: 1,
      icon: "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/partners/canyon.jpeg",
      title: "Canyon Coal",
      description: "Leading coal mining operations",
    },
    {
      id: 2,
      icon: "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/partners/eskom.jpeg",
      title: "Eskom",
      description: "South Africa's power utility",
    },
    {
      id: 3,
      icon: "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/partners/exxaro.jpeg",
      title: "Exxaro Resources",
      description: "Diversified mining company",
    },
    {
      id: 4,
      icon: "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/partners/xakwa.jpeg",
      title: "Xakwa",
      description: "Diversified mining company",
    },
    {
      id: 5,
      icon: "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/partners/mavuClient.jpg",
      title: "Mavungwane Colliery",
      description: "Coal operation",
    },
    {
      id: 6,
      icon: "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/partners/ThungelaCertifficate.png",
      title: "Thungela Resources",
      description:
        "Pure-play producer and exporter of thermal coal in South Africa",
    },
    {
      id: 7,
      icon: "https://pid2pa08ws8lrx2l.public.blob.vercel-storage.com/partners/mbuyeloCoal.jpg",
      title: "Mbuyelo Coal",
      description: "Mining operations in South Africa",
    },
  ];

  return (
    <div className="app">
      <Header companyName={companyName} />
      <Hero
        title="MINING EXCELLENCE </br> <span>COMFORT OF THE 4TH INDUSTRIAL REVOLUTION</span>"
        subtitle="Leading provider of contract mining, material handling, and transport logistics services with uncompromising commitment to safety, quality, and sustainability."
        ctaText="EXPLORE Our Services"
        stats={heroStats}
      />
      <About
        title="ABOUT TECHPIONEERS PROJECTS"
        description="Techpioneers strives to render services of the highest quality to its customers. 
        This is achieved through a team approach, the awareness of its employees that their best efforts are essentials and , therefore, appreciated, and the knowledge that ultimately everyone will benefit from the company's successes. 
        To achieve and sustain the highest standard and quality of service. We are dedicated to employing the right people for the jobs to deliver successful placements for both individuals and organization."
        subsections={aboutSubsections}
      />
      <Services
        primaryServices={primaryServices}
        equipmentRental={equipmentRental}
        isoImplementation={isoImplementation}
      />
      <Projects projects={projects} />
      <Team teammates={teammates} />
      <OurPartners partners={partners} />
      <CTA
        title="READY TO GET IN TOUCH?"
        description="Connect with Techpioneers today to discuss how we can optimize your mining operations with our 4th Industrial Revolution solutions and equipment."
        buttonText="REQUEST A CONSULTATION"
      />
      <Footer companyName={companyName} />
    </div>
  );
};

export default App;
